"use client";
import React, { useState, useRef } from "react";
import Image from "next/image";
import SummaryBar from "./SummaryBar";
import FicheAnchorNav, { useFicheSections } from "./FicheAnchorNav";
import type { IFormationDetail } from "@/types/formation";
import { getYoutubeId } from "@/lib/video";

interface FichePresentationProps {
  formation: IFormationDetail;
}

const FichePresentation: React.FC<FichePresentationProps> = ({ formation }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const sections = useFicheSections(formation.slug);

  const youtubeId = formation.video_url
    ? getYoutubeId(formation.video_url)
    : null;

  const handlePlay = () => {
    if (!youtubeId) {
      videoRef.current?.play().catch(() => {});
    }
    setIsPlaying(true);
  };

  return (
    <section
      id="presentation"
      className="text-center px-4 relative z-20 scroll-mt-36 lg:scroll-mt-24"
    >
      <div className="md:hidden flex items-center bg-white rounded-[40px] border-b border-[#40BFDE] py-3 px-4 justify-center absolute left-1/2 -translate-x-1/2 -top-5">
        <Image
          src="/assets/small-logo.svg"
          alt="small-logo"
          title="small-logo"
          aria-hidden="true"
          width={30}
          height={17}
        />
      </div>
      <div className="relative">
        {/* Summary row — mobile/tablet only; desktop shows it inside the hero. */}
        <SummaryBar formation={formation} className="lg:hidden md:-mt-7 z-20" />
        {/* Navigation Pills — same row the mobile sticky header pins (FicheStickyNav) */}
        <FicheAnchorNav
          sections={sections}
          className="xl:gap-5 md:gap-8 gap-1.5 lg:pt-0 md:pt-12 pt-8 pb-2"
        />
        <div className="mx-auto w-full max-w-146.75 pt-10 pb-6 md:pt-12">
          <h2 className="font-red-hat text-[30px] md:text-[40px] leading-10 md:leading-13.25 font-light text-[#1B365E] text-center pb-4">
            Présentation{" "}
            <span className="md:inline hidden">de la Formation</span>
          </h2>

          {formation.video_url ? (
            <div className="relative w-full mx-auto overflow-hidden rounded-2xl aspect-video bg-[#1B365E]/5">
              {/* Video */}
              {youtubeId ? (
                isPlaying && (
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`}
                    title={formation.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )
              ) : (
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  controls
                  playsInline
                  poster={formation.video_cover_url ?? undefined}
                >
                  <source src={formation.video_url} type="video/mp4" />
                  Votre navigateur ne prend pas en charge la lecture vidéo.
                </video>
              )}

              {/* Thumbnail Overlay */}
              {!isPlaying && (
                <button
                  type="button"
                  onClick={handlePlay}
                  className="group absolute inset-0 cursor-pointer bg-white"
                  aria-label="Lire la vidéo de présentation"
                >
                  <Image
                    src={
                      formation.video_cover_url ||
                      "/assets/video-thumbnail-0.png"
                    }
                    alt=""
                    aria-hidden="true"
                    fill
                    sizes="(min-width: 640px) 587px, 100vw"
                    className="object-cover"
                  />

                  <Image
                    src="/assets/icons/play.svg"
                    alt=""
                    aria-hidden="true"
                    width={56}
                    height={56}
                    className="absolute top-1/2 left-1/2 size-14 -translate-x-1/2 -translate-y-1/2 transition-transform duration-300 group-hover:scale-110"
                  />
                </button>
              )}
            </div>
          ) : formation.video_cover_url ? (
            <div className="relative w-full mx-auto overflow-hidden rounded-2xl aspect-video">
              <Image
                src={formation.video_cover_url}
                alt={formation.title}
                fill
                sizes="(min-width: 640px) 587px, 100vw"
                className="object-cover"
              />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default FichePresentation;
